import { DateTime } from 'luxon';
import { IOpeningTimes, IDayPeriod, ITimePeriod } from './installation.interface';

/**
 * Opening-hours logic, shared by mobile (UI), backend and the roaming gateways.
 * Canonical shape = camelCase (`periodBegin`/`periodEnd`, weekday 1-7 Mon..Sun),
 * i.e. the shape the OICP outbound mapper already consumes.
 *
 * Timezone/DST is resolved with luxon: `DateTime.weekday` is 1-7 Mon..Sun, which
 * matches the OCPI weekday numbering exactly. Runs the same in browser and Node.
 */

export type OpenStatus =
  | 'always' // 24/7
  | 'open'
  | 'closingSoon' // open, but closes within `closingSoonMinutes`
  | 'closedButChargeable' // closed by schedule, but charging is still allowed
  | 'closedBlocked' // closed and (probably) not accessible
  | 'unknown'; // no opening-hours data -> show nothing, never assume 24/7

export interface OpenState {
  status: OpenStatus;
  isOpen: boolean;
  /** Can the user charge right now? (open OR chargingWhenClosed). */
  chargeable: boolean;
  /** Local weekday in the station timezone, 1-7 Mon..Sun. For "today" highlighting. */
  weekday: number;
  /** Today's periods in the station timezone, sorted, as "HH:mm" strings. */
  todayPeriods: Array<{ begin: string; end: string }>;
  /** When currently open: the "HH:mm" it closes. */
  closesAt?: string;
  /** When closed: the next opening. dayOffset 0=today, 1=tomorrow, ... */
  opensAt?: { weekday: number; time: string; dayOffset: number };
}

export interface GetOpenStateOptions {
  now?: Date;
  /** Used when the installation has no stored timezone. Italy-only default. */
  fallbackTimeZone?: string;
  closingSoonMinutes?: number;
}

const DEFAULT_TZ = 'Europe/Rome';
const CLOSING_SOON_MIN = 30;
const MIN_PER_DAY = 24 * 60;

/** "HH:mm" -> minutes since midnight (0..1440). null if malformed. Accepts "24:00". */
function toMinutes(hhmm: unknown): number | null {
  if (typeof hhmm !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59 || (h === 24 && min !== 0)) return null;
  return h * 60 + min;
}

function fmtMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Local weekday (1-7 Mon..Sun) and minutes-since-midnight in `timeZone`, DST-aware. */
function localParts(now: Date, timeZone: string): { weekday: number; minutes: number } {
  let dt = DateTime.fromJSDate(now).setZone(timeZone);
  if (!dt.isValid) dt = DateTime.fromJSDate(now); // invalid IANA zone -> system local
  return { weekday: dt.weekday, minutes: dt.hour * 60 + dt.minute };
}

function toMs(d: Date | string | undefined): number | null {
  if (!d) return null;
  const ms = d instanceof Date ? d.getTime() : new Date(d).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function withinAny(periods: IOpeningTimes['exceptionalClosings'], nowMs: number): boolean {
  if (!Array.isArray(periods)) return false;
  return periods.some((p) => {
    const b = toMs(p?.periodBegin);
    const e = toMs(p?.periodEnd);
    return b != null && e != null && nowMs >= b && nowMs < e;
  });
}

function periodsForDay(regularHours: IDayPeriod[], weekday: number) {
  return regularHours
    .filter((p) => Number(p.weekday) === weekday)
    .map((p) => ({ b: toMinutes(p.periodBegin), e: toMinutes(p.periodEnd), begin: p.periodBegin, end: p.periodEnd }))
    .filter((p) => p.b != null && p.e != null) as Array<{ b: number; e: number; begin: string; end: string }>;
}

/** Is the schedule open at (weekday, minutes)? Handles periods that wrap past midnight. */
function openNowByRegular(
  regularHours: IDayPeriod[],
  weekday: number,
  minutes: number,
): { open: boolean; closesAtMin?: number } {
  // Today's periods.
  for (const p of periodsForDay(regularHours, weekday)) {
    if (p.e > p.b) {
      if (minutes >= p.b && minutes < p.e) return { open: true, closesAtMin: p.e };
    } else if (p.e < p.b) {
      // Wraps past midnight: [b, 24:00) belongs to today.
      if (minutes >= p.b) return { open: true, closesAtMin: p.e };
    }
  }
  // Yesterday's wrapping tail: [00:00, e) belongs to today.
  const yesterday = weekday === 1 ? 7 : weekday - 1;
  for (const p of periodsForDay(regularHours, yesterday)) {
    if (p.e < p.b && minutes < p.e) return { open: true, closesAtMin: p.e };
  }
  return { open: false };
}

/** Next opening from (weekday, minutes), scanning up to 7 days ahead. */
function nextOpening(
  regularHours: IDayPeriod[],
  weekday: number,
  minutes: number,
): { weekday: number; time: string; dayOffset: number } | undefined {
  for (let off = 0; off <= 7; off++) {
    const wd = ((weekday - 1 + off) % 7) + 1;
    const periods = periodsForDay(regularHours, wd).sort((a, b) => a.b - b.b);
    for (const p of periods) {
      if (off === 0 && p.b <= minutes) continue; // already passed today
      return { weekday: wd, time: p.begin, dayOffset: off };
    }
  }
  return undefined;
}

function hasNoData(o: IOpeningTimes): boolean {
  return (
    !o.twentyfourseven &&
    !(o.regular_hours && o.regular_hours.length) &&
    !(o.exceptionalOpenings && o.exceptionalOpenings.length) &&
    !(o.exceptionalClosings && o.exceptionalClosings.length)
  );
}

/**
 * Resolve the current open/closed state of an installation.
 * Returns `unknown` (render nothing) when there is no schedule data — we never
 * assume 24/7 from absence.
 */
export function getOpenState(
  openingTimes: IOpeningTimes | undefined | null,
  chargingWhenClosed: boolean | undefined,
  timeZone: string | undefined,
  opts: GetOpenStateOptions = {},
): OpenState {
  const now = opts.now ?? new Date();
  const tz = timeZone || opts.fallbackTimeZone || DEFAULT_TZ;
  const closingSoon = opts.closingSoonMinutes ?? CLOSING_SOON_MIN;
  const { weekday, minutes } = localParts(now, tz);
  const canCharge = chargingWhenClosed === true;

  if (!openingTimes || hasNoData(openingTimes)) {
    return { status: 'unknown', isOpen: false, chargeable: canCharge, weekday, todayPeriods: [] };
  }

  const regularHours = openingTimes.regular_hours ?? [];
  const todayPeriods = periodsForDay(regularHours, weekday)
    .sort((a, b) => a.b - b.b)
    .map((p) => ({ begin: p.begin, end: p.end }));

  // Precedence: exceptional closings override everything, then exceptional openings.
  const nowMs = now.getTime();
  let open = false;
  let closesAtMin: number | undefined;
  if (withinAny(openingTimes.exceptionalClosings, nowMs)) {
    open = false;
  } else if (withinAny(openingTimes.exceptionalOpenings, nowMs)) {
    open = true;
  } else if (openingTimes.twentyfourseven) {
    open = true;
  } else {
    const r = openNowByRegular(regularHours, weekday, minutes);
    open = r.open;
    closesAtMin = r.closesAtMin;
  }

  if (open) {
    const isAlways = openingTimes.twentyfourseven === true && closesAtMin == null;
    let remaining: number | undefined;
    if (closesAtMin != null) {
      remaining = closesAtMin > minutes ? closesAtMin - minutes : closesAtMin + MIN_PER_DAY - minutes;
    }
    const status: OpenStatus = isAlways
      ? 'always'
      : remaining != null && remaining <= closingSoon
        ? 'closingSoon'
        : 'open';
    return {
      status,
      isOpen: true,
      chargeable: true,
      weekday,
      todayPeriods,
      closesAt: closesAtMin != null ? fmtMinutes(closesAtMin) : undefined,
    };
  }

  return {
    status: canCharge ? 'closedButChargeable' : 'closedBlocked',
    isOpen: false,
    chargeable: canCharge,
    weekday,
    todayPeriods,
    opensAt: nextOpening(regularHours, weekday, minutes),
  };
}

/** Group regular hours into a Mon..Sun table for the detail view (empty = closed that day). */
export function getWeeklySchedule(
  openingTimes: IOpeningTimes | undefined | null,
): Array<{ weekday: number; periods: Array<{ begin: string; end: string }> }> {
  const regularHours = openingTimes?.regular_hours ?? [];
  const days: Array<{ weekday: number; periods: Array<{ begin: string; end: string }> }> = [];
  for (let wd = 1; wd <= 7; wd++) {
    const periods = periodsForDay(regularHours, wd)
      .sort((a, b) => a.b - b.b)
      .map((p) => ({ begin: p.begin, end: p.end }));
    days.push({ weekday: wd, periods });
  }
  return days;
}

/**
 * Normalize an opening-times object that may use OCPI snake_case
 * (`period_begin`/`period_end`, `exceptional_openings`) into the canonical
 * camelCase shape. Idempotent on already-canonical input.
 */
export function normalizeOpeningTimes(raw: any): IOpeningTimes | undefined {
  if (!raw || typeof raw !== 'object') return undefined;

  const rh = raw.regular_hours ?? raw.regularHours;
  const regular_hours: IDayPeriod[] | undefined = Array.isArray(rh)
    ? rh
        .map((d: any) => ({
          weekday: Number(d?.weekday),
          periodBegin: d?.periodBegin ?? d?.period_begin,
          periodEnd: d?.periodEnd ?? d?.period_end,
        }))
        .filter((d: IDayPeriod) => Number.isFinite(d.weekday) && !!d.periodBegin && !!d.periodEnd)
    : undefined;

  const mapExc = (arr: any) =>
    Array.isArray(arr)
      ? arr
          .map((p: any) => ({
            periodBegin: new Date(p?.periodBegin ?? p?.period_begin),
            periodEnd: new Date(p?.periodEnd ?? p?.period_end),
          }))
          .filter((p) => !Number.isNaN(p.periodBegin.getTime()) && !Number.isNaN(p.periodEnd.getTime()))
      : undefined;

  const out: IOpeningTimes = {};
  if (regular_hours && regular_hours.length) out.regular_hours = regular_hours;
  if (typeof raw.twentyfourseven === 'boolean') out.twentyfourseven = raw.twentyfourseven;
  const eo = mapExc(raw.exceptionalOpenings ?? raw.exceptional_openings);
  const ec = mapExc(raw.exceptionalClosings ?? raw.exceptional_closings);
  if (eo && eo.length) out.exceptionalOpenings = eo;
  if (ec && ec.length) out.exceptionalClosings = ec;

  return hasNoData(out) ? undefined : out;
}

const OICP_DAY_TO_INDEX: Record<string, number> = {
  Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7,
};
const OICP_DAY_GROUPS: Record<string, number[]> = {
  Everyday: [1, 2, 3, 4, 5, 6, 7],
  Workdays: [1, 2, 3, 4, 5],
  Weekend: [6, 7],
};

/**
 * Convert OICP opening times (`{ on, Period: [{begin,end}] }[]` + `IsOpen24Hours`)
 * into the canonical camelCase shape. Inverse of the OICP outbound `buildOpeningTimes`.
 */
export function oicpOpeningTimesToCanonical(
  oicp: Array<{ on: string; Period: Array<{ begin: string; end: string }> }> | undefined | null,
  isOpen24Hours?: boolean,
): IOpeningTimes | undefined {
  if (isOpen24Hours) return { twentyfourseven: true };
  if (!Array.isArray(oicp) || oicp.length === 0) return undefined;

  const regular_hours: IDayPeriod[] = [];
  for (const entry of oicp) {
    const days = OICP_DAY_GROUPS[entry?.on] ?? (OICP_DAY_TO_INDEX[entry?.on] != null ? [OICP_DAY_TO_INDEX[entry.on]] : []);
    const periods = Array.isArray(entry?.Period) ? entry.Period : [];
    for (const wd of days) {
      for (const p of periods) {
        if (p?.begin && p?.end) regular_hours.push({ weekday: wd, periodBegin: p.begin, periodEnd: p.end });
      }
    }
  }
  return regular_hours.length ? { regular_hours } : undefined;
}

/** OCPI snake_case opening-times shape (exceptional periods as ISO datetime strings, wire format). */
export interface OcpiOpeningTimes {
  regular_hours?: Array<{ weekday: number; period_begin: string; period_end: string }>;
  twentyfourseven?: boolean;
  exceptional_openings?: Array<{ period_begin: string; period_end: string }>;
  exceptional_closings?: Array<{ period_begin: string; period_end: string }>;
}

function toIso(d: Date | string | undefined): string {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toISOString();
}

/**
 * Canonical (camelCase) opening times -> OCPI snake_case shape. Inverse of `normalizeOpeningTimes`.
 * Returns `undefined` when there is nothing to publish — never emit an empty/24-7 default
 * (absence of data must NOT be turned into "always open").
 */
export function canonicalToOcpiOpeningTimes(
  o: IOpeningTimes | undefined | null,
): OcpiOpeningTimes | undefined {
  if (!o || hasNoData(o)) return undefined;

  const out: OcpiOpeningTimes = {};

  const regular = (o.regular_hours ?? [])
    .filter((d) => Number.isFinite(Number(d?.weekday)) && !!d?.periodBegin && !!d?.periodEnd)
    .map((d) => ({ weekday: Number(d.weekday), period_begin: d.periodBegin, period_end: d.periodEnd }));
  if (regular.length) out.regular_hours = regular;

  if (typeof o.twentyfourseven === 'boolean') out.twentyfourseven = o.twentyfourseven;

  const mapExc = (arr?: Array<ITimePeriod>) =>
    (arr ?? [])
      .map((p) => ({ period_begin: toIso(p?.periodBegin), period_end: toIso(p?.periodEnd) }))
      .filter((p) => !!p.period_begin && !!p.period_end);

  const eo = mapExc(o.exceptionalOpenings);
  const ec = mapExc(o.exceptionalClosings);
  if (eo.length) out.exceptional_openings = eo;
  if (ec.length) out.exceptional_closings = ec;

  return out;
}
